import { getBackendUrl } from "@Utils";
import axiosWrapper from "@AxiosWrapper";
import { statusCategories } from "@Features/portal-settings";
import { t } from "@i18n";
import { convertDate } from "@Services/luxonHelper";
import { reviewStageMapper } from "./ReviewsConstants";
import type {
  CreateReviewRequest,
  CreateReviewResponse,
  GetReviewDataRequest,
  GetReviewDataResponse,
  SaveUserResponseForReviewRequest,
  SaveUserResponseForReviewResponse,
  UserPermissionsToMarkReviewRequest,
  UserPermissionsToMarkReviewResponse,
  SendNotificationForReviewRequest,
  SendNotificationForReviewResponse,
  FetchListOfStatusesAvailableRequest,
  FetchListOfStatusesAvailableResponse,
  FetchBulkTransitionStatusListRequest,
  FetchBulkTransitionStatusListResponse,
  ChangeResourceStatusInReviewRequest,
  ChangeResourceStatusInReviewResponse,
  NotifyUsersInLevelRequest,
  NotifyUsersInLevelResponse,
  NotifyUserInReviewRequest,
  NotifyUserInReviewResponse,
  GetFileStatusInAllReviewsRequest,
  GetFileStatusInAllReviewsResponse,
  GetReviewStatusForAllFileVersionsRequest,
  GetReviewStatusForAllFileVersionsResponse,
  InitiateReviewRequest,
  InitiateReviewResponse,
  AddResourcesToReviewRequest,
  AddResourcesToReviewResponse,
  RemoveResourcesFromReviewRequest,
  RemoveResourcesFromReviewResponse,
  RollbackReviewRequest,
  RollbackReviewResponse,
  GetReviewsListForEntityRequest,
  GetReviewsListForEntityResponse,
  CanUserClearReviewLevelRequest,
  CanUserClearReviewLevelResponse,
  UpdateFilesInReviewRequest,
  UpdateFilesInReviewResponse,
  GetResourcesInReviewRequest,
  GetResourcesInReviewResponse,
  GetIssuesDetailsInReviewRequest,
  GetIssuesDetailsInReviewResponse,
  AddSignatureToResourcesRequest,
  AddSignatureToResourcesResponse,
  AddReferencedFilesInReviewsRequest,
  AddReferencedFilesInReviewsResponse,
  UpdateReviewStatusRequest,
  UpdateReviewStatusResponse,
  ChecklistsInReviewsRequest,
  ChecklistsInReviewsResponse,
  GetChecklistQuestionCommentsRequest,
  GetChecklistQuestionCommentsResponse,
  GetChecklistQuestionHistoryRequest,
  GetChecklistQuestionHistoryResponse,
  ManageChecklistRequest,
  ManageChecklistResponse,
} from "@ServerTypes/reviews/reviews.schema";
import type { GetCustomFieldsRequest, GetCustomFieldsResponse } from "@ServerTypes/issues/issues.schema";
import type { Exact } from "@Helpers/types/exact";

export const fileStatusHelper = (args: any): any => {
  const { resourceType } = args;
  const resource: any = { resourceType };

  if (resourceType === "RESOURCE_TYPE_FILE" || resourceType === "RESOURCE_TYPE_EMBED") {
    const { fileVersionId } = args;
    resource.fileVersionId = fileVersionId;
  }

  if (resourceType === "RESOURCE_TYPE_ESTIMATE") {
    const { estimateId, estimateVersionId } = args;
    resource.estimateVersionId = estimateVersionId;
    resource.estimateId = estimateId;
  }

  if (resourceType === "RESOURCE_TYPE_TASK") {
    const { taskId } = args;
    resource.taskId = taskId;
  }

  if (resourceType === "RESOURCE_TYPE_ISSUE") {
    const { issueId } = args;
    resource.issueId = issueId;
  }

  return resource;
};

export const getResourceDetails = (resources: any[]): any[] =>
  resources
    .filter(Boolean)
    .map((resource: any) => {
      const { resourceType } = resource;

      if (resourceType === "RESOURCE_TYPE_FILE" || resourceType === "RESOURCE_TYPE_EMBED") {
        const { fileVersion: fileVersionId, name } = resource;
        return { fileVersionId, resourceType, name };
      }

      if (resourceType === "RESOURCE_TYPE_MERGED_FILE") {
        const { fileVersion: fileVersionId } = resource;
        return { fileVersionId, resourceType };
      }

      if (resourceType === "RESOURCE_TYPE_ESTIMATE") {
        const { _id: estimateId, versionId: estimateVersionId } = resource;
        return { estimateId, estimateVersionId, resourceType };
      }

      if (resourceType === "RESOURCE_TYPE_ISSUE") {
        const { issueId } = resource;
        return { issueId, resourceType };
      }

      if (resourceType === "RESOURCE_TYPE_TASKS") {
        const { taskId } = resource;
        return { taskId, resourceType: "RESOURCE_TYPE_TASK" };
      }

      return null;
    })
    .filter(Boolean);

export const getApprovalStagesToShow = (
  approvalStages: any[],
  isRollback: boolean,
  skipReviewTillLevel: number,
  rollbackReviewToLevel: number,
): any => {
  let filteredStages = filterApprovalStages(approvalStages, isRollback, skipReviewTillLevel, rollbackReviewToLevel);

  const isReviewInitiated = filteredStages[0]?.clearanceStatus !== "pending";
  const willReviewClose = !isRollback && filteredStages.at(-1)?.clearanceStatus === "in_progress";

  const { startIndex, rollbackIndex } = getStartIndexAndRollbackIndex(
    filteredStages,
    isRollback,
    rollbackReviewToLevel,
  );

  const numberOfStagesToShow = isReviewInitiated ? 4 : 3;
  const endIndex = startIndex + numberOfStagesToShow;

  const showStartLine = startIndex >= 0;
  const showEndLine = endIndex < filteredStages.length;

  const approvalStagesToShow = filteredStages.slice(startIndex, endIndex).map((stage: any, idx: number) => ({
    ...stage,
    rollbackTo: rollbackIndex !== null && startIndex + idx === rollbackIndex,
  }));

  return {
    showStartLine,
    showEndLine,
    approvalStagesToShow,
    isReviewInitiated,
    willReviewClose,
  };
};

const filterApprovalStages = (
  approvalStages: any[],
  isRollback: boolean,
  skipReviewTillLevel: number,
  rollbackReviewToLevel: number,
): any[] => {
  let filteredStages = [...approvalStages];

  if (skipReviewTillLevel) {
    const approvalStageInProgressIndex = findApprovalStageInProgressIndex(filteredStages);
    filteredStages = filteredStages.filter(
      (stage, index) => index <= approvalStageInProgressIndex || index >= skipReviewTillLevel,
    );
  } else if (rollbackReviewToLevel && isRollback) {
    const approvalStageInProgressIndex = findApprovalStageInProgressIndex(filteredStages);
    const approvalStageApprovedIndex = filteredStages.findLastIndex(
      (stage: any) => stage.serial === rollbackReviewToLevel,
    );
    filteredStages = filteredStages.filter(
      (stage, index) => index <= approvalStageApprovedIndex || index >= approvalStageInProgressIndex,
    );
  } else if (isRollback) {
    const approvalStageInProgressIndex = findApprovalStageInProgressIndex(filteredStages);
    const approvalStageApprovedIndex = filteredStages.findLastIndex(
      (stage: any) => stage.clearanceStatus === "approved",
    );
    filteredStages = filteredStages.filter(
      (stage, index) => index < approvalStageApprovedIndex + 1 || index >= approvalStageInProgressIndex,
    );
  }

  return filteredStages;
};

const findApprovalStageInProgressIndex = (approvalStages: any[]): number => {
  return approvalStages.findIndex((stage: any) => stage.clearanceStatus === "in_progress");
};

const getStartIndexAndRollbackIndex = (
  approvalStages: any[],
  isRollback: boolean,
  rollbackReviewToLevel: number,
): { startIndex: number; rollbackIndex: any } => {
  let startIndex = 0;
  let rollbackIndex: any = null;

  const approvalStageInProgressIndex = findApprovalStageInProgressIndex(approvalStages);
  const totalStages = approvalStages.length;

  if (approvalStageInProgressIndex === -1 && approvalStages[0]?.clearanceStatus !== "pending") {
    startIndex = Math.max(totalStages - 4, 0);
  }

  if (approvalStageInProgressIndex === -1 || approvalStageInProgressIndex === 0) {
    startIndex = 0;
  }

  if (approvalStageInProgressIndex > 0) {
    if (isRollback && rollbackReviewToLevel) {
      rollbackIndex = rollbackReviewToLevel - 1;
    } else if (isRollback) {
      rollbackIndex = approvalStageInProgressIndex - 1;
    }

    const diff = totalStages - approvalStageInProgressIndex;

    if (diff === 1) {
      startIndex = Math.max(0, approvalStageInProgressIndex - 3);
    } else if (diff === 2) {
      startIndex = Math.max(0, approvalStageInProgressIndex - 2);
    } else if (diff > 2) {
      startIndex = Math.max(0, approvalStageInProgressIndex - 1);
    }
  }

  return { startIndex, rollbackIndex };
};

export const createNewReview = async (data: CreateReviewRequest) =>
  await axiosWrapper.post<CreateReviewResponse>(`${getBackendUrl()}/reviews/createReview`, data);

export const getReviewData = async (data: GetReviewDataRequest) =>
  await axiosWrapper.post<GetReviewDataResponse>(`${getBackendUrl()}/reviews/getReviewData`, data);

export const saveUserResponse = async (data: SaveUserResponseForReviewRequest) =>
  await axiosWrapper.post<SaveUserResponseForReviewResponse>(
    `${getBackendUrl()}/reviews/saveUserResponseForReview`,
    data,
  );

export const userPermissionsToMarkResponse = async (data: UserPermissionsToMarkReviewRequest) =>
  await axiosWrapper.post<UserPermissionsToMarkReviewResponse>(
    `${getBackendUrl()}/reviews/userPermissionsToMarkReview`,
    data,
  );

export const sendNotificationForReview = async (data: SendNotificationForReviewRequest) =>
  await axiosWrapper.post<SendNotificationForReviewResponse>(
    `${getBackendUrl()}/reviews/sendNotificationForReview`,
    data,
  );

export const fetchListOfStatusesAvailable = async (data: FetchListOfStatusesAvailableRequest) =>
  await axiosWrapper.post<FetchListOfStatusesAvailableResponse>(
    `${getBackendUrl()}/reviews/fetchListOfStatusesAvailable`,
    data,
  );

export const fetchBulkTransitionStatusList = async (data: FetchBulkTransitionStatusListRequest) =>
  await axiosWrapper.post<FetchBulkTransitionStatusListResponse>(
    `${getBackendUrl()}/reviews/fetchBulkTransitionStatusList`,
    data,
  );

export const changeResourceStatusInReview = async (data: ChangeResourceStatusInReviewRequest) =>
  await axiosWrapper.post<ChangeResourceStatusInReviewResponse>(
    `${getBackendUrl()}/reviews/changeResourceStatusInReview`,
    data,
  );

export const notifyUsersInLevel = async (data: NotifyUsersInLevelRequest) =>
  await axiosWrapper.post<NotifyUsersInLevelResponse>(`${getBackendUrl()}/reviews/notifyUsersInLevel`, data);

export const notifyUserInReview = async (data: NotifyUserInReviewRequest) =>
  await axiosWrapper.post<NotifyUserInReviewResponse>(`${getBackendUrl()}/reviews/notifyUserInReview`, data);

export const getFileStatusInReviews = async (data: GetFileStatusInAllReviewsRequest) =>
  await axiosWrapper.post<GetFileStatusInAllReviewsResponse>(
    `${getBackendUrl()}/reviews/getFileStatusInAllReviews`,
    data,
  );

export const getReviewStatusForAllFileVersions = async (data: GetReviewStatusForAllFileVersionsRequest) =>
  await axiosWrapper.post<GetReviewStatusForAllFileVersionsResponse>(
    `${getBackendUrl()}/reviews/getReviewStatusForAllFileVersions`,
    data,
  );

export const initiateReview = async (data: InitiateReviewRequest) =>
  await axiosWrapper.post<InitiateReviewResponse>(`${getBackendUrl()}/reviews/initiateReview`, data);

export const addResourcesToReview = async (data: AddResourcesToReviewRequest) =>
  await axiosWrapper.post<AddResourcesToReviewResponse>(`${getBackendUrl()}/reviews/addResourcesToReview`, data);

export const removeResourcesFromReview = async (data: RemoveResourcesFromReviewRequest) =>
  await axiosWrapper.post<RemoveResourcesFromReviewResponse>(
    `${getBackendUrl()}/reviews/removeResourcesFromReview`,
    data,
  );

export const getIssueCustomFields = async <T extends Exact<GetCustomFieldsRequest, T>>(data: T) =>
  await axiosWrapper.post<GetCustomFieldsResponse>(`${getBackendUrl()}/issues/getCustomFields`, data);

export const createStandaloneIssue = async <T extends Exact<GetCustomFieldsRequest, T>>(data: T) =>
  await axiosWrapper.post<GetCustomFieldsResponse>(`${getBackendUrl()}/issues/getCustomFields`, data);

export const rollbackReview = async (data: RollbackReviewRequest) =>
  await axiosWrapper.post<RollbackReviewResponse>(`${getBackendUrl()}/reviews/rollbackReview`, data);

export const getReviewsListForEntity = async (data: GetReviewsListForEntityRequest) =>
  await axiosWrapper.post<GetReviewsListForEntityResponse>(`${getBackendUrl()}/reviews/getReviewsListForEntity`, data);

export const canUserClearReviewLevel = async (data: CanUserClearReviewLevelRequest) =>
  await axiosWrapper.post<CanUserClearReviewLevelResponse>(`${getBackendUrl()}/reviews/canUserClearReviewLevel`, data);

export const updateFilesInReview = async (data: UpdateFilesInReviewRequest) =>
  await axiosWrapper.post<UpdateFilesInReviewResponse>(`${getBackendUrl()}/reviews/updateFilesInReview`, data);

export const getResourcesInReview = async (data: GetResourcesInReviewRequest) =>
  await axiosWrapper.post<GetResourcesInReviewResponse>(`${getBackendUrl()}/reviews/getResourcesInReviewService`, data);

export const getIssuesDetailsInReview = async (data: GetIssuesDetailsInReviewRequest) =>
  await axiosWrapper.post<GetIssuesDetailsInReviewResponse>(
    `${getBackendUrl()}/reviews/getIssuesDetailsInReviewService`,
    data,
  );

export const addSignatureToResources = async (data: AddSignatureToResourcesRequest) =>
  await axiosWrapper.post<AddSignatureToResourcesResponse>(`${getBackendUrl()}/reviews/addSignatureToResources`, data);

export const referencedFilesInReviews = async (
  data: AddReferencedFilesInReviewsRequest & { action?: "update" | "delete" },
) => {
  // Dispatch to the dedicated endpoints (split from the old action-dispatched /referencedFilesInReviews).
  const path =
    data.action === "delete" ? "/reviews/deleteReferencedFilesInReviews" : "/reviews/updateReferencedFilesInReviews";
  return await axiosWrapper.post<AddReferencedFilesInReviewsResponse>(`${getBackendUrl()}${path}`, data);
};

export const updateReviewStatus = async (data: UpdateReviewStatusRequest) => {
  return axiosWrapper.post<UpdateReviewStatusResponse>(`${getBackendUrl()}/reviews/updateReviewStatus`, data);
};

export const checklistsInReviews = async (data: ChecklistsInReviewsRequest) =>
  await axiosWrapper.post<ChecklistsInReviewsResponse>(`${getBackendUrl()}/reviews/checklistsInReviews`, data);

export const getChecklistQuestionComments = async (data: GetChecklistQuestionCommentsRequest) =>
  await axiosWrapper.post<GetChecklistQuestionCommentsResponse>(
    `${getBackendUrl()}/reviews/getChecklistQuestionComments`,
    data,
  );

export const getChecklistQuestionHistory = async (data: GetChecklistQuestionHistoryRequest) =>
  await axiosWrapper.post<GetChecklistQuestionHistoryResponse>(
    `${getBackendUrl()}/reviews/getChecklistQuestionHistory`,
    data,
  );

export const manageChecklist = async (data: ManageChecklistRequest) =>
  await axiosWrapper.post<ManageChecklistResponse>(`${getBackendUrl()}/reviews/manageChecklist`, data);

export const getReviewsGridColumns = ({
  creatorTemplate,
  createdDateTemplate,
  finishedDateTemplate,
  nextActionDueDateTemplate,
  waitingForTemplate,
  reviewSerialIdTemplate,
  reviewStageTemplate,
  workFlowTemplate,
  projectNameTemplate,
  navigateTemplate,
  showNavigateColumn = false,
}: any) => [
  ...(showNavigateColumn
    ? [
        {
          field: "id",
          id: "id",
          name: "",
          width: 50,
          formatter: (data: number, params: number, a: any, b: unknown, value: any) => navigateTemplate(value),
        },
      ]
    : []),
  {
    field: "serialId",
    id: "serialId",
    name: t("Id"),
    minWidth: 100,
    formatter: (data: number, params: number, a: any, b: unknown, value: any) => reviewSerialIdTemplate(value),
    _valueGetter: (params: any) => params.serialId,
    _excelValueGetter: (row: any) => `RE - ${row.serialId}`,
  },
  {
    field: "name",
    id: "name",
    name: t("Name"),
    minWidth: 130,
    filterType: "Text",
  },
  {
    field: "description",
    id: "description",
    name: t("Description"),
    minWidth: 130,
    filterType: "Text",
    visible: false,
  },
  {
    field: "reviewFlow",
    id: "reviewFlow",
    name: t("Review Flow"),
    minWidth: 120,
    formatter: (data: number, params: number, a: any, b: unknown, value: any) => workFlowTemplate(value),
    _valueGetter: (params: any) => params.reviewFlow?.name,
    _excelValueGetter: (row: any) => row.reviewFlow?.name || "-",
  },
  {
    field: "spaceName",
    id: "spaceName",
    minWidth: 130,
    name: t("Space"),
  },
  {
    field: "nextActionDueDate",
    id: "nextActionDueDate",
    name: t("Next Action Due"),
    minWidth: 150,
    formatter: (data: number, params: number, a: any, b: unknown, value: any) => nextActionDueDateTemplate(value),
    filterType: "Date",
    _valueGetter: (params: any) => convertDate(Number(params.nextActionDueDate), "dateForTooltip"),
    _excelValueGetter: (row: any) =>
      row.nextActionDueDate ? convertDate(Number(row.nextActionDueDate), "dateForTooltip") : "-",
  },
  {
    field: "currentReviewLevel",
    id: "currentReviewLevel",
    name: t("Waiting For"),
    minWidth: 90,
    formatter: (data: number, params: number, a: any, b: unknown, value: any) => waitingForTemplate(value),
    _valueGetter: (params: any) =>
      params.currentReviewLevel?.serial != null
        ? `${params.currentReviewLevel.serial} - ${params.currentReviewLevel.title}`
        : "-",
    _excelValueGetter: (row: any) =>
      row.currentReviewLevel?.serial != null
        ? `${row.currentReviewLevel.serial} - ${row.currentReviewLevel.title}`
        : "-",
  },
  {
    field: "template",
    id: "template",
    name: t("Project Name"),
    minWidth: 130,
    filterType: "Text",
    formatter: (data: number, params: number, a: any, b: unknown, value: any) => projectNameTemplate(value),
    _valueGetter: (params: any) => params.projectData?.description,
    _excelValueGetter: (row: any) => row.projectData?.description || "-",
  },
  {
    field: "finishedDate",
    id: "finishedDate",
    name: t("Finished On"),
    formatter: (data: number, params: number, a: any, b: unknown, value: any) => finishedDateTemplate(value),
    filterType: "Date",
    visible: false,
    minWidth: 130,
    _valueGetter: (params: any) => params.finishedDate,
    _excelValueGetter: (row: any) => (row.finishedDate ? convertDate(Number(row.finishedDate), "dateForTooltip") : "-"),
  },
  {
    field: "numberOfEntitiesAttached",
    id: "numberOfEntitiesAttached",
    name: t("Contents"),
    minWidth: 60,
    visible: false,
    _valueGetter: (params: any) => params.numberOfEntitiesAttached,
  },
  {
    field: "reviewStatus",
    id: "reviewStatus",
    name: t("Stage"),
    isReviewColumnType: true,
    minWidth: 130,
    formatter: (data: number, params: number, a: any, b: unknown, value: any) => reviewStageTemplate(value),
    filterType: "Text",
    visible: false,
    _valueGetter: (params: any) => params.reviewStatus,
    _excelValueGetter: (row: { reviewStatus: string }) =>
      reviewStageMapper[row.reviewStatus]?.name ?? row.reviewStatus ?? "-",
  },
  {
    field: "createdBy",
    id: "createdBy",
    name: t("Created by"),
    minWidth: 180,
    formatter: (data: number, params: number, a: any, b: unknown, value: any) => creatorTemplate(value),
    filterType: "Text",
    visible: false,
    _valueGetter: (params: any) => `${params.createdBy?.firstName ?? ""} ${params.createdBy?.lastName ?? ""}`.trim(),
    _excelValueGetter: (row: any) => `${row.createdBy?.firstName ?? ""} ${row.createdBy?.lastName ?? ""}`.trim() || "-",
  },
  {
    field: "createdDate",
    id: "createdDate",
    name: t("Created On"),
    minWidth: 150,
    formatter: (data: number, params: number, a: any, b: unknown, value: any) => createdDateTemplate(value),
    filterType: "Date",
    visible: false,
    _valueGetter: (params: any) => convertDate(Number(params.createdDate), "dateForTooltip"),
    _excelValueGetter: (row: any) => (row.createdDate ? convertDate(Number(row.createdDate), "dateForTooltip") : "-"),
  },
];

export const getSelectProjectsGridColumns = ({ projectImgDescTemplate }: any): any[] => [
  {
    field: "projectId",
    id: "ProjectId",
    minWidth: 150,
    formatter: (data: number, params: number, a: any, b: unknown, value: any) => projectImgDescTemplate(value),
    name: t("ID"),
    filterType: "Text",
    wrapText: true,
  },
  {
    field: "description",
    id: "description",
    minWidth: 150,
    name: t("Name"),
    filterType: "Text",
    wrapText: true,
  },
];

export const getSelectReviewsFlowsGridColumns = ({
  createdDateReviewFlowTemplate,
  createdByReviewFlowTemplate,
}: any) => [
  {
    field: "name",
    id: "name",
    name: t("Name"),
    filterType: "Text",
    wrapText: true,
  },
  {
    field: "description",
    id: "description",
    minWidth: 150,
    name: t("Description"),
    filterType: "Text",
    wrapText: true,
  },
  {
    field: "createdBy",
    id: "createdBy",
    name: t("Created By"),
    formatter: (data: number, params: number, a: any, b: unknown, value: any) => createdByReviewFlowTemplate(value),
  },
  {
    field: "createdDate",
    id: "createdDate",
    name: t("Created On"),
    formatter: (data: number, params: number, a: any, b: unknown, value: any) => createdDateReviewFlowTemplate(value),
    filterType: "Date",
  },
];

export const getFilesInReviewToolbar = ({
  addBtnTemplate,
  updateBtnTemplate,
  removeBtnTemplate,
  fileActivityBtnTemplate,
  bulkTransitionBtnTemplate,
  addSignatureToFileBtnTemplate,
  excelExportReviews,
  downloadReviewsFiles,
  allowSendTransmittals,
}: any): any[] => {
  const list: any[] = [
    { id: "add_file", template: addBtnTemplate },
    { id: "update_file", template: updateBtnTemplate },
    { id: "remove_file", template: removeBtnTemplate },
    { id: "file_activity", template: fileActivityBtnTemplate },
    { id: "add_signature", template: addSignatureToFileBtnTemplate },
    { id: "download_reviews_files", template: downloadReviewsFiles },
    { id: "bulk_transition", template: bulkTransitionBtnTemplate },
  ];
  if (allowSendTransmittals) {
    list.push({
      id: "excel_export_reviews",
      template: excelExportReviews,
    });
  }

  return list;
};

export const getIssuesInReviewToolbar = ({
  addBtnTemplate,
  removeBtnTemplate,
  // exportBtnTemplate,
  exportPDFBtnTemplate,
}: any): any[] => {
  const list: any[] = [
    { id: "add_issue", template: addBtnTemplate },
    { id: "remove_issue", template: removeBtnTemplate },
    // { id: "excel_export", template: exportBtnTemplate },
    { id: "pdf_export", template: exportPDFBtnTemplate },
  ];

  return list;
};

export const getFilesInReviewGridColumns = ({
  openFileInViewerTemplate,
  fileStatusTemplate,
  fileSizeTemplate,
  areIssuesPresentTemplate,
  fileVersionTemplate,
  fileTypeTemplate,
}: any): any[] => [
  {
    type: "checkbox",
    width: 40,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "fileVersionId",
    headerText: "",
    width: "0",
    visible: false,
    showInColumnChooser: false,
    isPrimaryKey: true,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "",
    width: "30",
    headerText: "",
    textAlign: "Center",
    template: openFileInViewerTemplate,
    editTemplate: openFileInViewerTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "",
    width: "30",
    headerText: "",
    textAlign: "Center",
    template: areIssuesPresentTemplate,
    editTemplate: areIssuesPresentTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "resourceType",
    headerText: "",
    width: "0",
    visible: false,
    showInColumnChooser: false,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "details.name",
    headerText: "Name",
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "fileType",
    headerText: "Type",
    template: fileTypeTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
    width: "60",
  },
  {
    field: "versionNumber",
    headerText: "Version",
    template: fileVersionTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
    width: "60",
  },
  {
    field: "size",
    headerText: "Size",
    template: fileSizeTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
    width: "60",
  },
  {
    field: "status",
    headerText: "Status",
    template: fileStatusTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
];

export const getIssuesInReviewGridColumns = ({
  openIssueInViewerTemplate,
  officialResponseTemplate,
  severityTemplate,
  issueImageTemplate,
  fileVersionTemplate,
  issueStatusTemplate,
  issueTitleTemplate,
  fileNameTemplate,
}: any): any[] => [
  {
    type: "checkbox",
    width: 50,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "issueId",
    headerText: "",
    width: "0",
    visible: false,
    showInColumnChooser: false,
    isPrimaryKey: true,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "resourceType",
    headerText: "",
    width: "0",
    visible: false,
    showInColumnChooser: false,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "",
    width: "30",
    headerText: "",
    textAlign: "Center",
    template: openIssueInViewerTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "snapshot",
    width: "100",
    headerText: "Image",
    textAlign: "Center",
    template: issueImageTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "title",
    headerText: "Title",
    template: issueTitleTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "officialResponse",
    headerText: "Official Response",
    template: officialResponseTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "severity",
    headerText: "Severity",
    template: severityTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "file",
    headerText: "File",
    template: fileNameTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "fileVersionNumber",
    headerText: "File Version",
    template: fileVersionTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "status",
    headerText: "Status",
    template: issueStatusTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
];

export const getResourcesActivityColumns = ({
  openFileInViewerTemplate,
  fileNameTemplate,
  fileSizeTemplate,
  versionTemplate,
  creatorTemplate,
  dateTemplate,
  statusTemplate,
  modificationTypeTemplate,
}: any): any[] => [
  {
    field: "fileVersionId",
    headerText: "",
    width: "0",
    visible: false,
    showInColumnChooser: false,
    isPrimaryKey: false,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "",
    headerText: "",
    width: "30",
    textAlign: "Center",
    template: openFileInViewerTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "fileName",
    headerText: "Name",
    template: fileNameTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "fileSize",
    headerText: "File Size",
    template: fileSizeTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "versionNumber",
    headerText: "Version",
    template: versionTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "updatedBy",
    headerText: "Modified by",
    template: creatorTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "updatedDate",
    headerText: "Modified Date",
    template: dateTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "status",
    headerText: "Last Status",
    template: statusTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "type",
    headerText: "Modification Type",
    template: modificationTypeTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
];

export const getStatusBtnStyle = (category: any): any => {
  const matchedCategory: any = statusCategories.find((record: any) => record.name === category);
  const { color, backgroundHexcode: backgroundColor } = matchedCategory;

  const style = { backgroundColor, color };
  return style;
};

export const getDropdownBackground = (category: any): any => {
  const matchedCategory: any = statusCategories.find((record: any) => record.name === category);
  const background = matchedCategory.background;
  return background;
};
